'use client'; // Error components must be client components

import React, { useEffect } from 'react';
import { Box, Heading, Text, Button, Center, VStack, Code } from '@chakra-ui/react';
import { FaExclamationTriangle } from 'react-icons/fa';
import StackTrace from 'stacktrace-js';

export default function Error({ error, reset }) {
  useEffect(() => {
    console.error('Global Error Boundary caught an error:', error);

    const sendErrorEmail = async () => {
      // Get current URL and referrer
      const currentUrl = window.location.href;
      const referrerUrl = document.referrer || "Kein Referrer verfügbar"; // document.referrer can be empty

      // Attempt to map the error stack trace using source maps
      let mappedStack = error.stack;
      try {
        const stackFrames = await StackTrace.fromError(error);
        mappedStack = stackFrames.map(sf => sf.toString()).join('\n');
      } catch (mappingError) {
        console.error('Error mapping stack trace:', mappingError);
        // Use original stack if mapping fails
        mappedStack = error.stack || "Stapelverfolgung konnte nicht ermittelt oder gemappt werden";
      }

      // Prepare payload with de-minified stack trace and URL info
      const payload = {
        errorMessage: error.message || "Keine Fehlermeldung verfügbar",
        errorStack: mappedStack,
        currentUrl: currentUrl, // Add current URL
        referrerUrl: referrerUrl, // Add referrer URL
      };

      try {
        const response = await fetch('/api/system/email/automaticerrorreport', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          console.error("Failed to send error email:", await response.text());
        } else {
          console.log("Error report sent successfully.");
        }
      } catch (emailError) {
        console.error("Error sending error email:", emailError);
      }
    };

    // Only attempt to send the email if not in development (optional, but good practice)
    // if (process.env.NODE_ENV === 'production') {
       sendErrorEmail();
    // } else {
    //   console.log("Skipping error email in development mode.");
    // }

  }, [error]); // Dependency array remains the same

  return (
    <Center height="100vh" padding="4" flexDirection="column">
      <VStack spacing={6} align="center" justify="center" flex="1">
        <Box as={FaExclamationTriangle} size="4rem" color="orange" />
        <Heading as="h2" size="xl" textAlign="center">
          Hoppla! Da ist etwas schiefgelaufen.
        </Heading>
        <Text color="gray.600" textAlign="center" maxW="md">
          Es ist ein unerwarteter Fehler in der Anwendung aufgetreten. Wir arbeiten daran!
          Bitte versuche, die Seite neu zu laden oder komm später wieder.
        </Text>
        <Button colorScheme="blue" onClick={() => reset()}>
          Seite neu laden
        </Button>
        <Text color="gray.500" textAlign="center" mt={4}>
          Wir werden automatisch über diesen Fehler informiert und arbeiten sofort an einer Lösung. Sie brauchen nichts weiter zu unternehmen. <br/>
          Vielen Dank für Ihr Verständnis.
        </Text>
      </VStack>

      {/* Keep displaying error details for debugging/user info */}
      <Box mt={8} maxW="container.md" overflow="auto" maxHeight="30vh" border="1px solid #ccc" borderRadius="md" padding="4" bg="gray.50">
        <Heading size="md" mb={2}>Fehlerdetails:</Heading>
        <Text fontWeight="bold">Fehlermeldung:</Text>
        <Code display="block" whiteSpace="pre-wrap" colorScheme="red" mb={2}>
          {error.message || "Keine Fehlermeldung verfügbar"}
        </Code>
        <Text fontWeight="bold">Stapelverfolgung:</Text>
        <Code display="block" whiteSpace="pre-wrap" colorScheme="gray" fontSize="sm">
          {error.stack || "Keine Stapelverfolgung verfügbar"}
        </Code>
      </Box>
    </Center>
  );
}